/**
 * Structural product data — cast-in-place concrete, reinforcing steel,
 * structural steel shapes and embedded/misc iron.
 *
 * Ships in the bundle so the structural catalog and estimate work offline.
 * Prices are seed values (national average supply pricing, 2025-26 era) and
 * hours are typical crew hours per unit to place, tie, erect or bolt the item.
 * Both are editable on the device and every total re-rolls from the edit.
 *
 * Sources for the specs quoted on each row:
 *   • Concrete mixes — ACI 318 / ASTM C94 ready-mix, 28-day f'c
 *   • Reinforcing bar — ASTM A615 Grade 60, nominal weights per ACI 318 App. A
 *   • Welded wire reinforcement — ASTM A1064
 *   • Structural shapes — AISC Steel Construction Manual, 15th ed. (ASTM A992/A36)
 *   • Bolts & anchors — ASTM F3125 Gr. A325, ACI 318 Ch. 17 anchorage
 *   • Deck — SDI (Steel Deck Institute) roof/floor deck profiles
 */

export type StructuralSection = "concrete" | "rebar" | "steel" | "misc";

export interface StructuralItem {
  /** stable catalog id, unique across all sections */
  id: string;
  section: StructuralSection;
  label: string;
  /** nominal size as it reads on a takeoff, or "—" */
  size: string;
  /** buying unit: cubic yard, foot, pound, square foot or each */
  unit: "cy" | "ft" | "lb" | "sqft" | "ea";
  /** seed price per unit, $ */
  price: number;
  /** crew hours per unit to install */
  hours: number;
  /** short spec line — standard, grade, weight */
  spec: string;
}

export const STRUCTURAL_SECTIONS: Array<{ id: StructuralSection; label: string }> = [
  { id: "concrete", label: "Concrete, forms & finish" },
  { id: "rebar", label: "Reinforcing steel" },
  { id: "steel", label: "Structural steel" },
  { id: "misc", label: "Anchors, bolts & misc iron" },
];

export const structuralUnitLabel = (u: StructuralItem["unit"]) =>
  u === "cy" ? "cu yd" : u === "sqft" ? "sq ft" : u;

/* ------------------------------------------------------------------ */
/* concrete — $ and hours per cubic yard (or sq ft of form / finish)   */
/* ------------------------------------------------------------------ */

const CONCRETE: Omit<StructuralItem, "section">[] = [
  { id: "mix-3000", size: "3000 psi", label: "Ready-mix concrete", unit: "cy", price: 165, hours: 0.85, spec: "ASTM C94 · f'c 3000 psi · 4\" slump" },
  { id: "mix-4000", size: "4000 psi", label: "Ready-mix concrete", unit: "cy", price: 178, hours: 0.85, spec: "ASTM C94 · f'c 4000 psi · air-entrained" },
  { id: "mix-5000", size: "5000 psi", label: "Ready-mix concrete", unit: "cy", price: 195, hours: 0.9, spec: "ASTM C94 · f'c 5000 psi · structural" },
  { id: "mix-grout", size: "non-shrink", label: "Non-shrink grout, base plates", unit: "cy", price: 620, hours: 4.0, spec: "ASTM C1107 Grade B · 5000 psi" },
  { id: "pump", size: "—", label: "Concrete pump / placement", unit: "cy", price: 22, hours: 0.15, spec: "Boom pump, includes wash-out" },

  { id: "form-wall", size: "—", label: "Wall forms, plywood & waler", unit: "sqft", price: 5.6, hours: 0.11, spec: "Contact area · 3 uses" },
  { id: "form-footing", size: "—", label: "Footing / grade beam forms", unit: "sqft", price: 4.2, hours: 0.09, spec: "Contact area · 2x lumber" },
  { id: "form-edge", size: "—", label: "Slab edge form", unit: "ft", price: 3.1, hours: 0.06, spec: "Screed edge, stakes & bracing" },
  { id: "vapor", size: "10 mil", label: "Under-slab vapor barrier", unit: "sqft", price: 0.38, hours: 0.004, spec: "ASTM E1745 Class A" },
  { id: "finish-broom", size: "—", label: "Slab finish, broom", unit: "sqft", price: 0.72, hours: 0.012, spec: "ACI 117 · F-number standard" },
  { id: "finish-trowel", size: "—", label: "Slab finish, hard trowel", unit: "sqft", price: 1.05, hours: 0.018, spec: "ACI 117 · power trowel" },
  { id: "cure-seal", size: "—", label: "Cure & seal compound", unit: "sqft", price: 0.22, hours: 0.004, spec: "ASTM C309 Type 1" },
];

/* ------------------------------------------------------------------ */
/* rebar — $ and hours per foot placed and tied (A615 Gr. 60)          */
/* ------------------------------------------------------------------ */

const REBAR: Omit<StructuralItem, "section">[] = [
  { id: "rb-3", size: "#3 (3/8\")", label: "Rebar, placed & tied", unit: "ft", price: 0.52, hours: 0.012, spec: "ASTM A615 Gr. 60 · 0.376 lb/ft" },
  { id: "rb-4", size: "#4 (1/2\")", label: "Rebar, placed & tied", unit: "ft", price: 0.88, hours: 0.014, spec: "ASTM A615 Gr. 60 · 0.668 lb/ft" },
  { id: "rb-5", size: "#5 (5/8\")", label: "Rebar, placed & tied", unit: "ft", price: 1.34, hours: 0.017, spec: "ASTM A615 Gr. 60 · 1.043 lb/ft" },
  { id: "rb-6", size: "#6 (3/4\")", label: "Rebar, placed & tied", unit: "ft", price: 1.92, hours: 0.021, spec: "ASTM A615 Gr. 60 · 1.502 lb/ft" },
  { id: "rb-7", size: "#7 (7/8\")", label: "Rebar, placed & tied", unit: "ft", price: 2.6, hours: 0.026, spec: "ASTM A615 Gr. 60 · 2.044 lb/ft" },
  { id: "rb-8", size: "#8 (1\")", label: "Rebar, placed & tied", unit: "ft", price: 3.4, hours: 0.031, spec: "ASTM A615 Gr. 60 · 2.670 lb/ft" },
  { id: "rb-9", size: "#9 (1 1/8\")", label: "Rebar, placed & tied", unit: "ft", price: 4.3, hours: 0.037, spec: "ASTM A615 Gr. 60 · 3.400 lb/ft" },
  { id: "rb-10", size: "#10 (1 1/4\")", label: "Rebar, placed & tied", unit: "ft", price: 5.45, hours: 0.043, spec: "ASTM A615 Gr. 60 · 4.303 lb/ft" },
  { id: "rb-11", size: "#11 (1 3/8\")", label: "Rebar, placed & tied", unit: "ft", price: 6.7, hours: 0.05, spec: "ASTM A615 Gr. 60 · 5.313 lb/ft" },

  { id: "wwr-6610", size: "6x6 W1.4", label: "Welded wire reinforcement", unit: "sqft", price: 0.46, hours: 0.006, spec: "ASTM A1064 · 6x6 W1.4/W1.4" },
  { id: "wwr-6644", size: "6x6 W2.9", label: "Welded wire reinforcement", unit: "sqft", price: 0.72, hours: 0.007, spec: "ASTM A1064 · 6x6 W2.9/W2.9" },
  { id: "chair", size: "—", label: "Bar chairs / bolsters", unit: "ea", price: 0.85, hours: 0.01, spec: "Plastic-tipped, slab & beam" },
  { id: "dowel", size: "3/4\" x 18\"", label: "Smooth dowel, slab joint", unit: "ea", price: 3.9, hours: 0.06, spec: "ASTM A615 plain · greased one end" },
];

/* ------------------------------------------------------------------ */
/* structural steel — $ and hours per foot of member (A992 / A36)      */
/* ------------------------------------------------------------------ */

const STEEL: Omit<StructuralItem, "section">[] = [
  { id: "w8x10", size: "W8x10", label: "Wide flange beam", unit: "ft", price: 16.5, hours: 0.07, spec: "AISC · ASTM A992 · 10 lb/ft" },
  { id: "w8x18", size: "W8x18", label: "Wide flange beam", unit: "ft", price: 28.5, hours: 0.08, spec: "AISC · ASTM A992 · 18 lb/ft" },
  { id: "w10x22", size: "W10x22", label: "Wide flange beam", unit: "ft", price: 34.5, hours: 0.09, spec: "AISC · ASTM A992 · 22 lb/ft" },
  { id: "w12x26", size: "W12x26", label: "Wide flange beam", unit: "ft", price: 40.5, hours: 0.1, spec: "AISC · ASTM A992 · 26 lb/ft" },
  { id: "w14x30", size: "W14x30", label: "Wide flange beam", unit: "ft", price: 47.0, hours: 0.11, spec: "AISC · ASTM A992 · 30 lb/ft" },
  { id: "w16x40", size: "W16x40", label: "Wide flange beam", unit: "ft", price: 62.0, hours: 0.13, spec: "AISC · ASTM A992 · 40 lb/ft" },
  { id: "w21x62", size: "W21x62", label: "Wide flange beam", unit: "ft", price: 96.0, hours: 0.17, spec: "AISC · ASTM A992 · 62 lb/ft" },

  { id: "hss4", size: "HSS 4x4x1/4", label: "Square tube column", unit: "ft", price: 24.0, hours: 0.09, spec: "AISC · ASTM A500 Gr. B · 12.2 lb/ft" },
  { id: "hss6", size: "HSS 6x6x3/8", label: "Square tube column", unit: "ft", price: 52.0, hours: 0.12, spec: "AISC · ASTM A500 Gr. B · 27.5 lb/ft" },
  { id: "c8", size: "C8x11.5", label: "Channel", unit: "ft", price: 19.5, hours: 0.08, spec: "AISC · ASTM A36 · 11.5 lb/ft" },
  { id: "l3", size: "L3x3x1/4", label: "Angle", unit: "ft", price: 9.4, hours: 0.06, spec: "AISC · ASTM A36 · 4.9 lb/ft" },
  { id: "l4", size: "L4x4x3/8", label: "Angle", unit: "ft", price: 16.2, hours: 0.07, spec: "AISC · ASTM A36 · 9.8 lb/ft" },
  { id: "plate", size: "—", label: "Plate & bar stock", unit: "lb", price: 1.85, hours: 0.02, spec: "ASTM A36 · shop cut & drilled" },
  { id: "joist", size: "—", label: "Open web steel joist", unit: "ft", price: 14.5, hours: 0.06, spec: "SJI K-series · erected & bridged" },
  { id: "deck-b", size: "1.5\" B deck", label: "Steel roof deck", unit: "sqft", price: 3.35, hours: 0.02, spec: "SDI · 22 ga galvanized" },
  { id: "deck-comp", size: "2\" composite", label: "Composite floor deck", unit: "sqft", price: 4.6, hours: 0.025, spec: "SDI · 20 ga galvanized" },
  { id: "shop-prime", size: "—", label: "Shop primer, structural", unit: "sqft", price: 1.15, hours: 0.01, spec: "SSPC-SP2 · alkyd primer" },
  { id: "fireproof", size: "—", label: "Spray fireproofing", unit: "sqft", price: 3.9, hours: 0.03, spec: "UL rated cementitious, 1 hr" },
];

/* ------------------------------------------------------------------ */
/* anchors, bolts & misc iron — $ and hours each                       */
/* ------------------------------------------------------------------ */

const MISC: Omit<StructuralItem, "section">[] = [
  { id: "ab-1-2", size: "1/2\" x 8\"", label: "Anchor bolt, cast-in", unit: "ea", price: 4.1, hours: 0.12, spec: "ASTM F1554 Gr. 36 · nut & washer" },
  { id: "ab-3-4", size: "3/4\" x 12\"", label: "Anchor bolt, cast-in", unit: "ea", price: 8.6, hours: 0.16, spec: "ASTM F1554 Gr. 36 · nut & washer" },
  { id: "ab-1", size: "1\" x 18\"", label: "Anchor bolt, cast-in", unit: "ea", price: 17.5, hours: 0.22, spec: "ASTM F1554 Gr. 55 · nut & washer" },
  { id: "wedge-1-2", size: "1/2\" x 4 3/4\"", label: "Wedge anchor, drilled", unit: "ea", price: 2.35, hours: 0.1, spec: "ACI 318 Ch. 17 · ICC-ES listed" },
  { id: "wedge-5-8", size: "5/8\" x 6\"", label: "Wedge anchor, drilled", unit: "ea", price: 3.9, hours: 0.13, spec: "ACI 318 Ch. 17 · ICC-ES listed" },
  { id: "epoxy-5-8", size: "5/8\" threaded rod", label: "Adhesive anchor set in epoxy", unit: "ea", price: 6.8, hours: 0.2, spec: "ACI 355.4 · cracked-concrete rated" },
  { id: "bolt-a325", size: "3/4\" A325", label: "Structural bolt, field", unit: "ea", price: 1.95, hours: 0.05, spec: "ASTM F3125 Gr. A325 · turn-of-nut" },
  { id: "base-plate", size: "—", label: "Column base plate, set & grout", unit: "ea", price: 145, hours: 1.4, spec: "A36 plate, leveling nuts, grout" },
  { id: "embed", size: "—", label: "Embed plate w/ studs", unit: "ea", price: 68, hours: 0.5, spec: "A36 plate · Nelson studs" },
  { id: "field-weld", size: "—", label: "Field weld, structural connection", unit: "ea", price: 12.5, hours: 0.35, spec: "AWS D1.1 · fillet, certified welder" },
  { id: "lintel", size: "L4x3x1/4", label: "Loose lintel, set", unit: "ea", price: 92, hours: 0.6, spec: "A36 galvanized, masonry bearing" },
  { id: "safety-rail", size: "—", label: "Pipe guard rail, fabricated", unit: "ft", price: 38, hours: 0.25, spec: "OSHA 1910.29 · 42\" two-rail" },
];

export const STRUCTURAL_ITEMS: StructuralItem[] = [
  ...CONCRETE.map((i) => ({ ...i, section: "concrete" as const })),
  ...REBAR.map((i) => ({ ...i, section: "rebar" as const })),
  ...STEEL.map((i) => ({ ...i, section: "steel" as const })),
  ...MISC.map((i) => ({ ...i, section: "misc" as const })),
];

const BY_ID = new Map(STRUCTURAL_ITEMS.map((i) => [i.id, i]));

export const getStructuralItem = (id: string) => BY_ID.get(id);

export const structuralItems = (section: StructuralSection) =>
  STRUCTURAL_ITEMS.filter((i) => i.section === section);
